import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Button, Heading, VStack, HStack, Text, Progress, Box, Flex, useBreakpointValue, Spinner, useToast } from '@chakra-ui/react';
import { useGame } from '../../contexts/GameContext.tsx';
import { useSound } from '../../contexts/SoundContext.tsx';
import { Player } from '../../types/game.ts'; // On garde les types
import MysteryTargetSelector from './MysteryTargetSelector.tsx';
import PowerRoulette from './PowerRoulette.tsx';
import { QuestionIcon, StarIcon, AtSignIcon } from '@chakra-ui/icons';
import '../../styles/animations.css';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TimerBar from '../Common/TimerBar.tsx';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const CategoryGrid: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { state, selectCategory, applyRoulettePower, clearAnswerResult } = useGame(); // On récupère l'état qui contient les catégories
  const { playSound, playCategorySound } = useSound();
  const toast = useToast();
  
  // Tous les hooks doivent être appelés avant les returns conditionnels
  const [isMysteryModalOpen, setIsMysteryModalOpen] = useState(false);
  const gridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3 });
  
  // Mélange des catégories quand le joueur est aveuglé, re-généré à chaque début de son tour
  const [shuffledIds, setShuffledIds] = useState<string[]>([]);
  const prevPlayerIdRef = useRef<number | null>(null);
  const shuffleArray = (ids: string[]) => {
    const arr = [...ids];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Important: Hooks must not be conditional; we guard logic inside
  useEffect(() => {
    // Pas de joueurs -> rien à faire
    if (!state.players.length) return;
    const cur = state.players[state.currentPlayerIndex];
    const eff = state.statusEffects?.[cur.id] || {};
    const blinded = typeof eff.blind === 'number' && eff.blind > 0;
    const isNewTurn = prevPlayerIdRef.current !== cur.id;
    if (isNewTurn) {
      prevPlayerIdRef.current = cur.id;
      if (blinded) {
        setShuffledIds(shuffleArray(state.categories.map(c => c.id)));
      } else {
        setShuffledIds([]);
      }
    } else {
      if (blinded && shuffledIds.length !== state.categories.length) {
        setShuffledIds(shuffleArray(state.categories.map(c => c.id)));
      }
    }
  }, [state.players, state.currentPlayerIndex, state.statusEffects, state.categories, shuffledIds.length]);

  // Local mode category timer hooks must be declared before any early returns
  const enableCategoryTimer = !!state.advanced?.enableCategoryTimer;
  const categoryTimerSeconds = Math.max(5, Math.min(120, state.advanced?.categoryTimerSeconds || 20));
  const [categoryDeadline, setCategoryDeadline] = useState<number | null>(null);

  // Start a new deadline at each new turn if enabled and no question showing
  useEffect(() => {
    if (!enableCategoryTimer) { setCategoryDeadline(null); return; }
    if (state.currentQuestion) { setCategoryDeadline(null); return; }
    const cur = state.players[state.currentPlayerIndex];
    if (!cur) { setCategoryDeadline(null); return; }
    setCategoryDeadline(Date.now() + categoryTimerSeconds * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPlayerIndex, enableCategoryTimer, categoryTimerSeconds, !!state.currentQuestion]);

  // Handle expiry: auto-pick a random available non-mystery, non-completed, non-blocked category
  useEffect(() => {
    if (!enableCategoryTimer || !categoryDeadline) return;
    const id = window.setInterval(() => {
      if (Date.now() >= categoryDeadline) {
        window.clearInterval(id);
        const cur = state.players[state.currentPlayerIndex];
        if (!cur) { setCategoryDeadline(null); return; }
        const effects = state.statusEffects?.[cur.id] || {};
        const candidates = state.categories.filter(c => {
          if (c.is_mystery) return false;
          const isCompleted = cur.completedCategories.includes(c.id);
          const isBlocked = (effects.blocked && effects.blocked[c.id] > 0) || false;
          return !isCompleted && !isBlocked;
        });
        const choice = candidates.length ? candidates[Math.floor(Math.random() * candidates.length)] : null;
        if (choice) {
          playSound('buttonClick');
          selectCategory(choice.id, i18n.language);
        }
        setCategoryDeadline(null);
      }
    }, 200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryDeadline, enableCategoryTimer, state.players, state.currentPlayerIndex, state.categories, state.statusEffects]);

  // Affichage d'un chargement si les catégories ne sont pas encore arrivées de l'API
  if (state.areCategoriesLoading) {
    return <Spinner size="xl" color="var(--bleu-electrique)" thickness="4px" />;
  }
  
  if (!state.players.length) return null; // Sécurité si les joueurs ne sont pas initialisés

  const currentPlayer = state.players[state.currentPlayerIndex];
  const currentEffects = state.statusEffects?.[currentPlayer.id] || {};
  const isBlindedCurrent = typeof currentEffects.blind === 'number' && currentEffects.blind > 0;
  
  const categoriesToRender = isBlindedCurrent && shuffledIds.length
    ? shuffledIds
        .map(id => state.categories.find(c => c.id === id))
        .filter((c): c is typeof state.categories[number] => Boolean(c))
    : state.categories;

  const getAttackablePlayers = () => {
    const otherPlayers = state.players.filter(p => p.id !== currentPlayer.id);
    return otherPlayers.filter(player => 
      Object.entries(player.score).some(([categoryId, score]) => {
        const category = state.categories.find(c => c.id === categoryId); // Utilise state.categories
        if (!category || category.is_mystery) return false;
        
        const isCompleted = player.completedCategories.includes(categoryId);
        return score > 0 && !isCompleted;
      })
    );
  };

  // Vérifier si le joueur actuel a au moins 1 point de streak non-complété (nécessaire pour attaquer)
  const hasMinimumStreakToAttack = () => {
    if (!currentPlayer) return false;
    
    return Object.entries(currentPlayer.score).some(([categoryId, score]) => {
      const category = state.categories.find(c => c.id === categoryId);
      if (!category || category.is_mystery) return false;
      
      // Vérifier que la catégorie n'est pas complètement validée
      const isCompleted = currentPlayer.completedCategories.includes(categoryId);
      return (score as number) > 0 && !isCompleted;
    });
  };

  const attackablePlayers = getAttackablePlayers();
  const canUseMystery = attackablePlayers.length > 0 && hasMinimumStreakToAttack();

  const handleCategoryClick = (categoryId: string) => {
    const category = state.categories.find(c => c.id === categoryId); // Utilise state.categories
    // Bloqué ?
    const isBlocked = currentEffects.blocked && currentEffects.blocked[categoryId] > 0;
    if (isBlocked && !category?.is_mystery) return; // ignore clicks on blocked categories

    // Jouer le son spécifique à la catégorie
    playCategorySound(categoryId);
    
  if (category?.is_mystery) {
      if (canUseMystery) {
        setIsMysteryModalOpen(true);
      } else {
        // Vérifier la raison spécifique pourquoi l'attaque n'est pas possible
        if (!hasMinimumStreakToAttack()) {
          toast({
            title: t('mysteryAttackBlocked'),
            description: t('mysteryAttackMinStreakRequired'),
            status: 'warning',
            duration: 4000,
            isClosable: true,
            position: 'top',
          });
        } else if (attackablePlayers.length === 0) {
          toast({
            title: t('mysteryAttackBlocked'),
            description: t('mysteryAttackNoTargets'),
            status: 'info',
            duration: 4000,
            isClosable: true,
            position: 'top',
          });
        }
        console.log('❌ Impossible d\'utiliser la catégorie mystère');
      }
    } else {
      // Toujours interroger l'API dans la langue UI actuelle, la traduction se fait côté client si effet actif
      selectCategory(categoryId, i18n.language);
    }
  };

  const handleMysteryTargetSelected = () => {
    setIsMysteryModalOpen(false);
    // Toujours poser la question mystère d'abord (comme en multi)
    selectCategory('mystery', i18n.language);
  };

  const handleRouletteSelected = (power: any) => {
    // Appliquer sur la bonne personne selon le résultat
    const applyTo = pendingForAttacker() ? 'target' : 'attacker';
    applyRoulettePower({ id: power.id, value: power.value }, applyTo);
    // Pas de toast ici pour ne pas spoiler/perturber l'animation
  };

  const pendingForAttacker = () => {
    // Si l'attaquant a gagné la question, la roulette vise la cible, sinon elle vise l'attaquant
    return !!state.pendingRoulette?.isAttackerWinner;
  };

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  return (
    <MotionBox initial="hidden" animate="visible" variants={containerVariants} w="full" maxW="1200px" p={{ base: 4, md: 8 }}>
      <VStack spacing={{ base: 6, md: 8 }} w="full">
        <Heading size={{ base: "lg", md: "xl" }} color="var(--texte-clair)" textAlign="center" mb={4}>
          {t('gameTitle', { count: state.players.length })}
        </Heading>
        
        <Flex direction={{ base: "column", md: "row" }} gap={{ base: 4, md: 6 }} w="full" justify="center" align="stretch">
          {state.players.map((player: Player) => {
            const eff = state.statusEffects[player.id] || {};
            const flashes = state.flashEffects?.[player.id] || [];
            const badges: Array<{ key: string; label: string; bg: string; emoji: string; counter?: number }> = [];
            if (eff.blind && eff.blind > 0) badges.push({ key: 'blind', label: 'Aveugle', bg: '#2D3748', emoji: '👁️‍🗨️', counter: eff.blind });
            if (eff.translate && eff.translate.remaining > 0) badges.push({ key: 'translate', label: eff.translate.lang.toUpperCase(), bg: '#234E52', emoji: '🌍', counter: eff.translate.remaining });
            if ((eff as any).hardcore && (eff as any).hardcore > 0) badges.push({ key: 'hardcore', label: 'Hardcore', bg: '#553C9A', emoji: '🔥', counter: (eff as any).hardcore });
            const blockedCount = eff.blocked ? Object.values(eff.blocked).filter(n => (n as number) > 0).length : 0;
            if (blockedCount > 0) badges.push({ key: 'blocked', label: `${blockedCount} bloc`, bg: '#742A2A', emoji: '🚫', counter: Math.max(...Object.values(eff.blocked || {}).map(n => (n as number))) });
            // One-turn flash indicators
            flashes.forEach(f => {
              // Parse dynamic ids: minusN, plusN, swapN
              const match = /^(minus|plus|swap)(\d+)$/.exec(f.id);
              if (match) {
                const kind = match[1];
                const n = Number(match[2]);
                if (kind === 'minus') {
                  badges.push({ key: `flash-${f.id}`, label: `-${n}`, bg: '#744210', emoji: '⬇️' });
                } else if (kind === 'plus') {
                  badges.push({ key: `flash-${f.id}`, label: `+${n}`, bg: '#22543D', emoji: '⬆️' });
                } else if (kind === 'swap') {
                  badges.push({ key: `flash-${f.id}`, label: `Vol ${n}`, bg: '#2C5282', emoji: '🔄' });
                }
                return;
              }
              const map: any = { blocked: { emoji: '🚫', label: 'Bloqué', bg: '#63171B' }, blind: { emoji: '👁️‍🗨️', label: 'Aveugle', bg: '#1A365D' }, translate: { emoji: '🌍', label: 'Traduction', bg: '#1C4532' }, nuke: { emoji: '💣', label: 'Nuke', bg: '#4A5568' }, megaswap: { emoji: '🌪️', label: 'MegaSwap', bg: '#2B6CB0' }, hardcore: { emoji: '🔥', label: 'Hardcore', bg: '#7B341E' } };
              const m = map[f.id] || { emoji: '✨', label: f.id, bg: '#2D3748' };
              badges.push({ key: `flash-${f.id}`, label: m.label, bg: m.bg, emoji: m.emoji });
            });
            return (
            <MotionFlex key={player.id} variants={itemVariants} flex="1" p={6} bg={player.id === currentPlayer.id ? "var(--nuit-profonde)" : "var(--gris-bleute)"} borderRadius="xl" border="2px solid" borderColor={player.id === currentPlayer.id ? "var(--bleu-electrique)" : "rgba(139, 148, 179, 0.2)"} direction="column" align="center" justify="center" position="relative" style={{ transition: "all 0.3s" }}>
              {player.id === currentPlayer.id && (<Box position="absolute" top={2} right={2} color="var(--bleu-electrique)"><AtSignIcon boxSize="1.5em" /></Box>)}
              <Text fontSize="xl" fontWeight="bold" color="var(--texte-clair)" mb={2}>{player.name}</Text>
              {badges.length > 0 && (
                <HStack spacing={2} wrap="wrap" justify="center" mb={2}>
                  {badges.map(b => (
                    <HStack key={b.key} spacing={1} px={2} py={1} borderRadius="full" bg={b.bg} border="1px solid rgba(255,255,255,0.1)" boxShadow="md">
                      <Text fontSize="xs">{b.emoji}</Text>
                      <Text fontSize="xs" color="white">{b.label}{typeof b.counter === 'number' ? ` · ${b.counter}` : ''}</Text>
                    </HStack>
                  ))}
                </HStack>
              )}
              <HStack spacing={2} align="center">
                <Box color="var(--magenta-vif)"><StarIcon boxSize="1.2em" /></Box>
                <Text fontSize="md" color="var(--texte-secondaire)">
                  {t('categoriesScore', { 
                    completed: player.completedCategories.filter(catId => {
                        const category = state.categories.find(c => c.id === catId); // Utilise state.categories
                        return category ? !category.is_mystery : false;
                    }).length,
                    total: state.categoriesToWin 
                  })}
                </Text>
              </HStack>
            </MotionFlex>
          )})}
        </Flex>

        {/* Timer de sélection de catégorie – sous les cartes joueurs (local) */}
        {enableCategoryTimer && categoryDeadline && (
          <Box w="full" maxW="800px" px={4}>
            <TimerBar
              deadline={categoryDeadline}
              seconds={categoryTimerSeconds}
              color={'var(--bleu-electrique)'}
              labelPrefix={t('selectCategory') || 'Choix catégorie'}
            />
          </Box>
        )}

        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" textAlign="center" color="var(--texte-clair)" mb={4}>
          {t('playerTurn', { name: currentPlayer.name })}
        </Text>

        <Grid templateColumns={`repeat(${gridColumns}, 1fr)`} gap={{ base: 4, md: 6 }} w="full">
          {categoriesToRender.map((category) => {
            const streak = currentPlayer.score[category.id] || 0;
            const isCompleted = currentPlayer.completedCategories.includes(category.id);
            
            // Gérer les couleurs personnalisées vs couleurs Chakra UI
            const getColorScheme = (color: string) => {
              if (!color) return 'blue';
              // Si c'est une couleur hex ou autre format CSS, on utilise une couleur par défaut
              if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('hsl')) {
                return 'blue'; // Couleur par défaut pour les couleurs personnalisées
              }
              // Si c'est un format Chakra UI (ex: "blue.400"), on prend la partie avant le point
              return color.split('.')[0];
            };

            const getCustomButtonStyles = (color: string) => {
              if (!color || !color.startsWith('#')) return {};
              return {
                backgroundColor: color,
                borderColor: color,
                _hover: {
                  backgroundColor: color,
                  opacity: 0.8,
                },
                _active: {
                  backgroundColor: color,
                  opacity: 0.9,
                }
              };
            };

            const colorScheme = getColorScheme(category.color);
            const customStyles = getCustomButtonStyles(category.color);
            
            // Effets visuels: blind masque noms/progresse
            const isBlocked = currentEffects.blocked && currentEffects.blocked[category.id] > 0;
            const blinded = isBlindedCurrent;
            const blindOnThis = blinded && !category.is_mystery;
            return (
              <MotionBox key={category.id} variants={itemVariants} whileHover={!isCompleted ? { scale: 1.03 } : undefined}>
                <VStack spacing={3} bg="var(--gris-bleute)" p={4} borderRadius="xl" boxShadow="md" style={{ transition: "transform 0.3s" }}>
                  <Button 
                    onClick={() => handleCategoryClick(category.id)} 
                    colorScheme={isCompleted ? 'green' : (blindOnThis ? 'gray' : colorScheme)}
                    size="lg" 
                    height={{ base: "80px", md: "100px" }} 
                    width="full" 
                    isDisabled={isBlocked || (isCompleted && !category.is_mystery) || (category.is_mystery && !canUseMystery) || state.isQuestionLoading} 
                    isLoading={state.isQuestionLoading && state.loadingCategoryId === category.id} 
                    className={isCompleted ? 'category-completed' : ''}
                    position="relative"
                    {...(blindOnThis
                      ? { 
                          backgroundColor: 'var(--gris-bleute)',
                          borderColor: 'rgba(255,255,255,0.12)',
                          _hover: { backgroundColor: 'var(--gris-bleute)', opacity: 0.95 },
                          _active: { backgroundColor: 'var(--gris-bleute)', opacity: 1 }
                        }
                      : (category.color && category.color.startsWith('#') ? customStyles : {})
                    )}
                  >
                    <HStack spacing={2}>
                      {category.is_mystery && <QuestionIcon boxSize="1.2em" />}
                      {category.is_custom && !category.is_mystery && (
                        <Text fontSize="md">{blinded ? '❔' : (category.emoji || '👤')}</Text>
                      )}
                      <Text>{blindOnThis ? '???' : category.name}</Text>
                      {isCompleted && !category.is_mystery && ' ✓'}
                    </HStack>
                    {isBlocked && !category.is_mystery && (
                      <Box position="absolute" top={2} left={2} fontSize="lg" title={t('blockedCategoryTooltip') || 'Catégorie bloquée'}>
                        🚫
                      </Box>
                    )}
                  </Button>
                  
                  {category.is_mystery && (
                    <Box w="full">
                      <Text fontSize="xs" color="var(--texte-secondaire)" textAlign="center">{canUseMystery ? t('attackablePlayers', { count: attackablePlayers.length }) : t('noAttackablePlayers')}</Text>
                      {!canUseMystery && (<Text fontSize="xs" color="red.400" textAlign="center">{t('noActiveStreaks')}</Text>)}
                    </Box>
                  )}
                  
                  {!category.is_mystery && (
                    <Box w="full">
                      {!blindOnThis ? (
                        <>
                          <Progress 
                            value={(streak / state.streakToWin) * 100} 
                            width="full" 
                            colorScheme={colorScheme}
                            borderRadius="full" 
                            size="sm" 
                            hasStripe={streak > 0} 
                            isAnimated={streak > 0}
                            sx={category.color && category.color.startsWith('#') ? {
                              '& > div': {
                                backgroundColor: category.color + '40', // Fond semi-transparent
                              },
                              '& > div > div': {
                                backgroundColor: category.color, // Barre de progression
                              }
                            } : {}}
                          />
                          <Text fontSize="sm" color="var(--texte-secondaire)" textAlign="center" mt={1}>{t('streakLabel')}: {streak} / {state.streakToWin}</Text>
                        </>
                      ) : (
                        <Text fontSize="sm" color="var(--texte-secondaire)" textAlign="center" mt={1}>Streak: ???</Text>
                      )}
                      {!blindOnThis && category.is_custom && category.creator_name && (
                        <Text fontSize="xs" color="var(--texte-secondaire)" textAlign="center" mt={1} opacity={0.8}>
                          par {category.creator_name}
                        </Text>
                      )}
                    </Box>
                  )}
                </VStack>
              </MotionBox>
            );
          })}
        </Grid>

        <MysteryTargetSelector 
          isOpen={isMysteryModalOpen}
          onClose={() => setIsMysteryModalOpen(false)} 
          onTargetSelected={handleMysteryTargetSelected}
        />

        {/* Roulette des pouvoirs (mode local) */}
        <PowerRoulette
          isOpen={state.mysteryAttackMode === 'roulette' && !!state.pendingRoulette}
          onClose={() => { /* contrôle piloté par applyRoulettePower qui nettoie pendingRoulette */ }}
          onPowerSelected={handleRouletteSelected}
          maxStreakLoss={3}
          targetPlayerName={state.players.find(p => p.id === state.mysteryTarget?.playerId)?.name || ''}
          attackerPlayerName={state.players[state.currentPlayerIndex]?.name || ''}
          isAttackerWinner={pendingForAttacker()}
        />
      </VStack>
    </MotionBox>
  );
};

export default CategoryGrid;